import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { LOGIN } from "@/constants/testIds/auth";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignIn() {
  const redirect = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-linen" data-testid="login-page">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=900&q=80" className="w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-charcoal/30" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="w-10 h-10 rounded-2xl rose-glow flex items-center justify-center mb-4 shadow-lg">
            <Sparkles className="w-5 h-5" strokeWidth={2} />
          </div>
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/80 mb-6">CelebrAI · Every celebration, perfectly planned.</p>
          <p className="font-display text-3xl leading-tight">"Small gestures, remembered well, become a life of love."</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="text-sm text-charcoal/60 hover:text-charcoal" data-testid="back-home">← Home</Link>
          <h1 className="font-display text-4xl mt-6 mb-2 text-charcoal">Welcome back</h1>
          <p className="text-charcoal/70 mb-8">Sign in to plan the next celebration.</p>

          <button onClick={googleSignIn} type="button" data-testid="google-login-btn" className="w-full pill-btn pill-btn-secondary flex items-center justify-center gap-3 mb-4">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6 text-charcoal/40 text-sm">
            <div className="h-px bg-warm flex-1" /> or <div className="h-px bg-warm flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
                data-testid="login-email" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
                data-testid="login-password" />
              <div className="flex justify-end mt-2">
                <Link to="/forgot-password" className="text-sm terracotta font-semibold hover:underline" data-testid={LOGIN.forgotPasswordLink}>Forgot Password?</Link>
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="login-submit"
              className="w-full pill-btn pill-btn-primary mt-2">{busy ? "Signing in..." : "Sign in"}</button>
          </form>

          <p className="text-sm text-charcoal/60 text-center mt-6">
            New here? <Link to="/signup" className="terracotta font-semibold" data-testid="link-signup">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
